import React, { useMemo } from "react";
import { BarChart3, Bot, CreditCard, Paintbrush, Sparkles } from "lucide-react";
import { tierIndex, TIER_KEYS, type TierKey } from "@reach/core";
import { merchantFetch } from "../api";
import { PLAN_DETAILS } from "../planData";
import type { SettingsAnalyticsGroup, SettingsBillingGroup, SettingsFormGroup, SettingsKbGroup, SettingsModalsGroup, SettingsTrainingGroup, SettingsUiGroup } from "./useSettingsPageState";

type TabItem = { id: string; content: string };

type UseSettingsPageViewModelParams = {
  form: Pick<SettingsFormGroup, "density" | "dirtySections" | "formState">;
  kb: Pick<SettingsKbGroup, "kbGaps" | "kbItems" | "kbItemsPerPage" | "kbPage" | "kbSearch" | "gapPage" | "gapItemsPerPage" | "scrapeUrl" | "setSelectedKbId">;
  training: Pick<SettingsTrainingGroup, "setIsTrainingModalOpen" | "setQContent" | "setQCustomReply" | "setQQuestion" | "setQSuggestions" | "setQKeywords" | "setQSource" | "setQTargetUrl">;
  billing: Pick<SettingsBillingGroup, "setPendingPlan" | "setPendingProviderTier">;
  analytics: Pick<SettingsAnalyticsGroup, "analyticsData" | "setIsRefreshing">;
  modals: Pick<SettingsModalsGroup, "setIsModalOpen" | "setShowAnalyticsWipeModal" | "setShowProviderModal" | "setShowWipeModal">;
  ui: Pick<SettingsUiGroup, "selectedTab" | "setSelectedTab">;
  showPhase2Tiers: boolean;
  shopDomain: string;
  tabs: TabItem[];
  fetchAnalytics: () => Promise<void> | void;
  fetchKb: () => Promise<void> | void;
  fetchQueryLog: () => Promise<void> | void;
  openNewTopicModal: () => void;
  setLoading: (value: boolean) => void;
  showToast: (msg: string) => void;
};

export function useSettingsPageViewModel({
  form,
  kb,
  training,
  billing,
  analytics,
  modals,
  ui,
  showPhase2Tiers,
  shopDomain,
  tabs,
  fetchAnalytics,
  fetchKb,
  fetchQueryLog,
  openNewTopicModal,
  setLoading,
  showToast,
}: UseSettingsPageViewModelParams) {
  const currentTier: TierKey = form.formState.planTier || "free";
  const level = tierIndex(currentTier);
  const isFree = currentTier === "free";
  const isGrowthPlus = level >= 1;
  const isAgentPlus = level >= 2;
  const isRepPlus = level >= 3;
  const visiblePlanIds = (showPhase2Tiers ? [...TIER_KEYS] : TIER_KEYS.filter((tier) => !["rep", "vea"].includes(tier))) as TierKey[];

  const safeSelectedTab = ui.selectedTab < tabs.length ? ui.selectedTab : 0;
  const activeTabId = tabs[safeSelectedTab]?.id ?? "assistant";

  const tabHeroMeta: Record<string, { title: string; subtitle: string; insight?: string; icon: React.ReactNode }> = {
    assistant: {
      title: "Train a sharper assistant",
      subtitle: "Improve match quality by filling gaps and refining high-traffic topics.",
      insight: kb.kbGaps.length > 0 ? `${kb.kbGaps.length} unanswered question${kb.kbGaps.length === 1 ? "" : "s"} spotted` : "Knowledge base coverage looks healthy",
      icon: <Bot size={16} />,
    },
    analytics: {
      title: "See what visitors are doing",
      subtitle: "Track engagement, resolution quality, and conversion-driving interactions.",
      insight: analytics.analyticsData ? `${analytics.analyticsData.widgetOpens ?? 0} widget opens in this period` : "Insights update as traffic lands",
      icon: <BarChart3 size={16} />,
    },
    plans: {
      title: "Shape your growth plan",
      subtitle: "Adjust billing, compare tiers, and align capabilities with store volume.",
      insight: `Current tier: ${PLAN_DETAILS[currentTier]?.name ?? currentTier}`,
      icon: <CreditCard size={16} />,
    },
    designer: {
      title: "Prototype premium storefront experiences",
      subtitle: "Design conversion-focused widget interactions before launch.",
      icon: <Sparkles size={16} />,
    },
    "visual-editor": {
      title: "Shape the widget experience",
      subtitle: "Adjust layout, button styling, and interactive behavior.",
      icon: <Paintbrush size={16} />,
    },
  };

  const filteredKbItems = useMemo(() => {
    if (!kb.kbSearch) return kb.kbItems;
    const search = kb.kbSearch.toLowerCase();
    return kb.kbItems.filter(
      (item) =>
        (item.title || "").toLowerCase().includes(search) ||
        (item.question || "").toLowerCase().includes(search) ||
        (item.reply || "").toLowerCase().includes(search) ||
        (item.customReply || "").toLowerCase().includes(search) ||
        (item.content || "").toLowerCase().includes(search)
    );
  }, [kb.kbItems, kb.kbSearch]);

  const paginatedKbItems = useMemo(() => {
    const start = (kb.kbPage - 1) * kb.kbItemsPerPage;
    return filteredKbItems.slice(start, start + kb.kbItemsPerPage);
  }, [filteredKbItems, kb.kbItemsPerPage, kb.kbPage]);

  const paginatedGapItems = useMemo(() => {
    const start = (kb.gapPage - 1) * kb.gapItemsPerPage;
    return kb.kbGaps.slice(start, start + kb.gapItemsPerPage);
  }, [kb.gapItemsPerPage, kb.gapPage, kb.kbGaps]);

  const dirtyMessage = (() => {
    const names = tabs.filter((tab) => form.dirtySections.has(tab.id)).map((tab) => tab.content);
    if (names.length === 0) return "You've made some changes";
    if (names.length === 1) return `You've made changes to ${names[0]}`;
    const last = names.pop();
    return `You've made changes to ${names.join(", ")} and ${last}`;
  })();

  const goToTab = (tabId: string) => ui.setSelectedTab(tabs.findIndex((tab) => tab.id === tabId));

  const assistantActions = {
    onCopyMasterPrompt: (masterPrompt: string) => {
      navigator.clipboard.writeText(masterPrompt);
      showToast("Prompt copied to clipboard!");
    },
    onEditKbItem: (item: any) => {
      kb.setSelectedKbId(item.id);
      training.setQQuestion(item.title || item.question || "");
      training.setQCustomReply(item.reply || item.customReply || "");
      training.setQSuggestions(item.suggestions || "");
      training.setQKeywords(item.keywords || "");
      training.setQSource(item.source || "manual");
      training.setQTargetUrl(item.targetUrl || (item.sourceUrl && /^https?:\/\//i.test(item.sourceUrl) ? item.sourceUrl : ""));
      training.setQContent(item.content || "");
      training.setIsTrainingModalOpen(true);
    },
    onOpenNewTopicModal: openNewTopicModal,
    onOpenPlansTab: () => goToTab("plans"),
    onScanPage: async () => {
      if (!kb.scrapeUrl) return;
      setLoading(true);
      const res = await merchantFetch(`/api/merchant/scrape`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ domain: shopDomain, url: kb.scrapeUrl }),
      });
      if (res.ok) {
        showToast("Scan complete! Check library below.");
        fetchKb();
      } else {
        const data = await res.json().catch(() => ({}));
        showToast(data.error || "Scan failed. Check the URL and try again.");
      }
      setLoading(false);
    },
    onShowWipeModal: () => modals.setShowWipeModal(true),
    onTrainGap: (gap: any) => {
      training.setQQuestion(gap.question);
      training.setQCustomReply("");
      training.setQSuggestions("");
      training.setQKeywords("");
      training.setQSource("manual");
      training.setQTargetUrl("");
      training.setQContent("");
      training.setIsTrainingModalOpen(true);
    },
  };

  const analyticsActions = {
    onOpenAppearanceTab: () => goToTab("visual-editor"),
    onOpenPlansTab: () => goToTab("plans"),
    onRefresh: async () => {
      analytics.setIsRefreshing(true);
      await Promise.all([fetchAnalytics(), fetchQueryLog()]);
      analytics.setIsRefreshing(false);
    },
    onShowAnalyticsWipeModal: () => modals.setShowAnalyticsWipeModal(true),
  };

  const subscriptionActions = {
    onOpenPlanModal: (planId: TierKey) => {
      billing.setPendingPlan(planId);
      modals.setIsModalOpen(true);
    },
    onOpenProviderModal: (planId: TierKey) => {
      billing.setPendingProviderTier(planId);
      modals.setShowProviderModal(true);
    },
  };

  return {
    activeTabId,
    analyticsActions,
    assistantActions,
    currentTier,
    dirtyMessage,
    filteredKbItems,
    isAgentPlus,
    isFree,
    isGrowthPlus,
    isRepPlus,
    paginatedGapItems,
    paginatedKbItems,
    safeSelectedTab,
    subscriptionActions,
    tabHeroMeta,
    visiblePlanIds,
  };
}
